import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import {
  ClipboardCheck,
  Sparkles,
  CheckCircle2,
  Copy,
  ExternalLink,
  Check,
  AlertTriangle,
  Loader2,
  Plus,
  Minus,
  Star,
  StarOff,
  Globe,
  List,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { ProposalLocale } from "../../proposal-standard/i18n/types";
import {
  SUPPORTED_LOCALES,
  LOCALE_LABELS,
  LOCALE_FLAGS,
} from "../../proposal-standard/i18n/types";
import { DEFAULT_ROUTES } from "@/constants/routes";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useAuth } from "@/features/authentication";
import { sendTrackDataServer } from "@/features/analytics/utils/serverTrackData";
import { reportError } from "@/utils/observability";
import { visionService } from "@/services/vision-service";
import { createToast } from "vercel-toast";
import type { AIModeState, ProposalPayload } from "../types";
import { VersionConflictError } from "../errors";
import { buildProposalPayload } from "../utils/buildProposalPayload";
import { getProposalUrl } from "../../utils/proposal-url";
import {
  computeProposalDiff,
  type ProposalDiff,
  type SpaceChanges,
  type FieldChange,
} from "../utils/computeProposalDiff";

type SaveStatus = "idle" | "saving" | "success" | "conflict" | "error";

interface ReviewStepProps {
  state: AIModeState;
  editingProposal: ProposalPayload;
  onValidityChange?: (valid: boolean) => void;
  registerNext?: (fn: () => void) => void;
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

const SPACE_ROW_CONFIG = {
  added: {
    borderColor: "#16A34A",
    bg: "#F0FDF4",
    Icon: Plus,
    iconColor: "#16A34A",
    badge: "NOVA",
    badgeBg: "#DCFCE7",
    badgeColor: "#15803D",
  },
  removed: {
    borderColor: "#DC2626",
    bg: "#FEF2F2",
    Icon: Minus,
    iconColor: "#DC2626",
    badge: "REMOVIDA",
    badgeBg: "#FEE2E2",
    badgeColor: "#B91C1C",
  },
  recommended: {
    borderColor: "#D97706",
    bg: "#FFFBEB",
    Icon: Star,
    iconColor: "#D97706",
    badge: "RECOMENDADA",
    badgeBg: "#FEF3C7",
    badgeColor: "#92400E",
  },
  unrecommended: {
    borderColor: "#9CA3AF",
    bg: "#F9FAFB",
    Icon: StarOff,
    iconColor: "#9CA3AF",
    badge: "RECOMENDAÇÃO REMOVIDA",
    badgeBg: "#F3F4F6",
    badgeColor: "#6B7280",
  },
} as const;

function SpaceRow({
  name,
  room,
  type,
}: {
  name: string;
  room: string;
  type: "added" | "removed" | "recommended" | "unrecommended";
}) {
  const c = SPACE_ROW_CONFIG[type];
  const Icon = c.Icon;

  return (
    <div
      className="flex items-center gap-3 px-3 py-2.5 rounded-xl mb-2"
      style={{ background: c.bg, borderLeft: `4px solid ${c.borderColor}` }}
    >
      <Icon className="w-4 h-4 flex-shrink-0" style={{ color: c.iconColor }} />
      <div className="flex-1 min-w-0">
        <p
          className="text-[11px] font-semibold truncate"
          style={{ color: "#1A1814" }}
        >
          {room}
        </p>
        <p className="text-[10px] truncate" style={{ color: "#9E9A94" }}>
          {name}
        </p>
      </div>
      <span
        className="flex-shrink-0 text-[9px] font-bold uppercase px-2 py-0.5 rounded-full"
        style={{ background: c.badgeBg, color: c.badgeColor }}
      >
        {c.badge}
      </span>
    </div>
  );
}

function FieldChangeRow({ change }: { change: FieldChange }) {
  const isLong = change.from.length > 40 || change.to.length > 40;

  if (isLong) {
    return (
      <div
        className="px-3 py-2.5 rounded-xl mb-2"
        style={{ background: "#FFFBEB", border: "1px solid #FEF3C7" }}
      >
        <span
          className="text-[11px] font-semibold block mb-2"
          style={{ color: "#78736D" }}
        >
          {change.label}
        </span>
        <div className="space-y-1.5">
          <p
            className="text-xs whitespace-pre-wrap break-words"
            style={{ color: "#9CA3AF", textDecoration: "line-through" }}
          >
            {change.from || "—"}
          </p>
          <div className="flex items-center gap-1.5">
            <div className="h-px flex-1" style={{ background: "#FEF3C7" }} />
            <span className="text-[10px]" style={{ color: "#C4B04A" }}>
              ↓
            </span>
            <div className="h-px flex-1" style={{ background: "#FEF3C7" }} />
          </div>
          <p
            className="text-xs font-semibold whitespace-pre-wrap break-words"
            style={{ color: "#1A1814" }}
          >
            {change.to || "—"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex items-start gap-2 px-3 py-2 rounded-xl mb-1.5"
      style={{ background: "#FFFBEB", border: "1px solid #FEF3C7" }}
    >
      <span
        className="text-[11px] font-semibold flex-shrink-0 pt-px"
        style={{ color: "#78736D" }}
      >
        {change.label}
      </span>
      <div className="flex items-center gap-1 flex-wrap flex-1 justify-end">
        <span
          className="text-[11px] break-words"
          style={{ color: "#9CA3AF", textDecoration: "line-through" }}
        >
          {change.from || "—"}
        </span>
        <span className="text-[10px]" style={{ color: "#9CA3AF" }}>
          →
        </span>
        <span
          className="text-[11px] font-semibold break-words"
          style={{ color: "#1A1814" }}
        >
          {change.to || "—"}
        </span>
      </div>
    </div>
  );
}

function SpaceAdjustmentSection({ sc }: { sc: SpaceChanges }) {
  return (
    <div className="mb-3">
      <p className="text-xs font-semibold mb-1.5" style={{ color: "#1A1814" }}>
        {sc.roomName}{" "}
        <span className="font-normal" style={{ color: "#9E9A94" }}>
          · {sc.spaceName}
        </span>
      </p>
      {sc.changes.map((c, i) => (
        <FieldChangeRow key={`${c.label}-${i}`} change={c} />
      ))}
    </div>
  );
}

function DiffSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className="rounded-2xl overflow-hidden mb-4"
      style={{ border: "1px solid #E8E6E3" }}
    >
      <div
        className="px-4 py-2.5"
        style={{ background: "#F8F7F6", borderBottom: "1px solid #E8E6E3" }}
      >
        <span
          className="text-xs font-bold uppercase tracking-wider"
          style={{ color: "#43403D" }}
        >
          {title}
        </span>
      </div>
      <div className="px-4 py-3">{children}</div>
    </div>
  );
}

function SuccessView({
  proposalUrl,
  versionNumber,
  companyName,
}: {
  proposalUrl: string;
  versionNumber: number | null;
  companyName: string | null | undefined;
}) {
  const navigate = useNavigate();
  const [linkCopied, setLinkCopied] = useState(false);
  const [selectedLang, setSelectedLang] = useState<ProposalLocale>("pt");
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    };
  }, []);

  const proposalUrlWithLang = useMemo(() => {
    if (selectedLang === "pt") return proposalUrl;
    return `${proposalUrl}?lang=${selectedLang}`;
  }, [proposalUrl, selectedLang]);

  async function handleCopy() {
    const ok = await copyToClipboard(proposalUrlWithLang);
    if (!ok) {
      createToast("Não foi possível copiar o link.", {
        type: "error",
        timeout: 3000,
      });
      return;
    }
    if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    setLinkCopied(true);
    copyTimeoutRef.current = setTimeout(() => {
      setLinkCopied(false);
      copyTimeoutRef.current = null;
    }, 3000);
  }

  return (
    <div
      className="space-y-3"
      style={{ animation: "aiStepEnter 0.4s cubic-bezier(0.16,1,0.3,1)" }}
    >
      <div
        className="rounded-2xl p-5 text-center"
        style={{
          background:
            "linear-gradient(135deg, rgba(255,217,0,0.09), rgba(255,217,0,0.02))",
          border: "1.5px solid rgba(255,217,0,0.22)",
        }}
      >
        <div
          className="w-14 h-14 rounded-full mx-auto flex items-center justify-center mb-3"
          style={{
            background: "#FFD900",
            animation: "successBounce 0.55s cubic-bezier(0.16,1,0.3,1)",
          }}
        >
          <CheckCircle2 className="w-7 h-7" style={{ color: "#1A1814" }} />
        </div>
        <p className="text-sm font-semibold mb-1" style={{ color: "#1A1814" }}>
          Proposta de{" "}
          <span style={{ fontStyle: "italic" }}>
            {companyName ?? "cliente"}
          </span>{" "}
          atualizada!
        </p>
        {versionNumber != null && (
          <span
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold"
            style={{ background: "#FEF08A", color: "#713F12" }}
          >
            V{versionNumber}
          </span>
        )}
      </div>

      <div
        className="flex items-center gap-3 px-4 py-3 rounded-xl"
        style={{ background: "#F5F3EF", border: "1px solid #E8E6E3" }}
      >
        <div className="flex items-center gap-1.5">
          <Globe className="w-3.5 h-3.5" style={{ color: "#78736D" }} />
          <span className="text-xs font-medium" style={{ color: "#78736D" }}>
            Idioma da proposta
          </span>
        </div>
        <div
          className="ml-auto inline-flex rounded-lg overflow-hidden"
          style={{ border: "1px solid #E8E6E3" }}
        >
          {SUPPORTED_LOCALES.map((loc) => (
            <button
              key={loc}
              type="button"
              onClick={() => {
                setSelectedLang(loc);
                setLinkCopied(false);
              }}
              className="px-3 py-1.5 text-xs font-semibold transition-all"
              style={{
                background: selectedLang === loc ? "#1A1814" : "white",
                color: selectedLang === loc ? "white" : "#78736D",
                border: "none",
                cursor: "pointer",
              }}
            >
              {LOCALE_FLAGS[loc]} {LOCALE_LABELS[loc]}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Button
          type="button"
          onClick={handleCopy}
          className="w-full gap-1.5"
          style={
            linkCopied
              ? {
                  background: "#FFD900",
                  color: "#1A1814",
                  borderColor: "#FFD900",
                }
              : {
                  background: "#1A1814",
                  color: "white",
                  borderColor: "#1A1814",
                }
          }
        >
          {linkCopied ? (
            <>
              <Check className="w-4 h-4" />
              Copiado com sucesso
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" />
              Copiar link da proposta
            </>
          )}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="w-full gap-1.5"
          onClick={() => window.open(proposalUrlWithLang, "_blank")}
        >
          <ExternalLink className="w-4 h-4" />
          Ver proposta
        </Button>
        <Button
          type="button"
          variant="outline"
          className="w-full gap-1.5"
          onClick={() => navigate(DEFAULT_ROUTES.VISION_360_PROPOSALS)}
        >
          <List className="w-4 h-4" />
          Voltar para a listagem
        </Button>
      </div>
    </div>
  );
}

export default function ReviewStep({
  state,
  editingProposal,
  onValidityChange,
  registerNext,
}: ReviewStepProps) {
  const navigate = useNavigate();
  const { userInfo } = useAuth();
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [savedProposalUrl, setSavedProposalUrl] = useState<string | null>(null);
  const [savedVersionNumber, setSavedVersionNumber] = useState<number | null>(
    null,
  );
  const [conflictVersion, setConflictVersion] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const onValidityChangeRef = useRef(onValidityChange);
  onValidityChangeRef.current = onValidityChange;
  const isSavingRef = useRef(false);

  const allSpaces = useMemo(
    () => [
      ...(state.location?.rankedSpaces ?? []),
      ...(editingProposal.state.location?.rankedSpaces ?? []),
    ],
    [state, editingProposal],
  );

  const diff: ProposalDiff = useMemo(
    () => computeProposalDiff(editingProposal.state, state, allSpaces),
    [editingProposal.state, state, allSpaces],
  );

  useEffect(() => {
    onValidityChangeRef.current?.(diff.hasChanges);
  }, [diff.hasChanges]);

  const handleSave = useCallback(async () => {
    if (isSavingRef.current) return;
    isSavingRef.current = true;
    setSaveStatus("saving");
    setErrorMessage(null);

    try {
      const base = buildProposalPayload(state, userInfo);
      const { created_by: _omit, ...rest } = base;
      const payload = {
        ...rest,
        updated_by: {
          user_id: userInfo?.user?.id ?? null,
          email: userInfo?.profile?.email ?? null,
          full_name: userInfo?.user
            ? `${userInfo.user.first_name} ${userInfo.user.last_name}`.trim()
            : null,
        },
        ...(editingProposal.versionNumber != null
          ? { expected_version: editingProposal.versionNumber }
          : {}),
      };

      const result = await visionService.updateProposal(
        editingProposal.uuid,
        payload,
      );

      if (result.noChanges) {
        createToast("Nenhuma alteração detectada.", {
          type: "success",
          timeout: 4000,
        });
      }

      const url = getProposalUrl(editingProposal.uuid);
      const newVersion = result.proposal?.versionNumber ?? null;

      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(url).catch(() => {});
      }

      sendTrackDataServer({
        eventName: "VISION_AI_MODE_PROPOSAL_EDIT_SAVED",
        productType: "PO",
        url: window.location.pathname,
        data: {
          proposal_uuid: editingProposal.uuid,
          company_name: state.diagnosis?.companyName ?? null,
          new_version_number: newVersion,
          no_changes: result.noChanges,
          changes_count: diff.totalCount,
        },
      });

      setSavedProposalUrl(url);
      setSavedVersionNumber(newVersion);
      setSaveStatus("success");
    } catch (err) {
      if (err instanceof VersionConflictError) {
        setConflictVersion(err.currentVersion);
        setSaveStatus("conflict");
        sendTrackDataServer({
          eventName: "VISION_AI_MODE_PROPOSAL_EDIT_CONFLICT",
          productType: "PO",
          url: window.location.pathname,
          data: {
            proposal_uuid: editingProposal.uuid,
            expected_version: editingProposal.versionNumber ?? null,
            current_version: err.currentVersion ?? 0,
          },
        });
      } else {
        reportError(err, {
          operation: "updateProposal",
          step: "review",
          proposalId: editingProposal.uuid,
        });
        setErrorMessage("Ocorreu um erro ao salvar. Tente novamente.");
        setSaveStatus("error");
      }
    } finally {
      isSavingRef.current = false;
    }
  }, [state, userInfo, editingProposal, diff.totalCount]);

  useEffect(() => {
    registerNext?.(() => handleSave());
  }, [registerNext, handleSave]);

  if (saveStatus === "success" && savedProposalUrl) {
    return (
      <SuccessView
        proposalUrl={savedProposalUrl}
        versionNumber={savedVersionNumber}
        companyName={state.diagnosis?.companyName}
      />
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <span
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold mb-4"
          style={{
            background: "rgba(255,217,0,0.1)",
            border: "1px solid rgba(255,217,0,0.25)",
            color: "#1A1814",
          }}
        >
          <ClipboardCheck className="w-3.5 h-3.5" />
          Resumo
        </span>
        <h2
          className="text-2xl font-bold mt-4 mb-1"
          style={{ color: "#1A1814" }}
        >
          Revisão das alterações
        </h2>
        <div className="flex items-center gap-2 mt-1">
          <p className="text-sm" style={{ color: "#78736D" }}>
            Verifique tudo antes de salvar
          </p>
          {diff.hasChanges ? (
            <span
              className="px-2.5 py-0.5 rounded-full text-[11px] font-bold"
              style={{ background: "#FEF3C7", color: "#92400E" }}
            >
              {diff.totalCount}{" "}
              {diff.totalCount !== 1 ? "alterações" : "alteração"}
            </span>
          ) : (
            <span
              className="px-2.5 py-0.5 rounded-full text-[11px] font-bold"
              style={{ background: "#F0EEEB", color: "#78736D" }}
            >
              Sem alterações
            </span>
          )}
        </div>
      </div>

      {/* No changes state */}
      {!diff.hasChanges && (
        <div
          className="rounded-2xl p-6 text-center"
          style={{ background: "#F8F7F6", border: "1px solid #E8E6E3" }}
        >
          <CheckCircle2
            className="w-10 h-10 mx-auto mb-3"
            style={{ color: "#9E9A94" }}
          />
          <p
            className="text-sm font-semibold mb-1"
            style={{ color: "#43403D" }}
          >
            Nenhuma alteração detectada
          </p>
          <p className="text-xs" style={{ color: "#9E9A94" }}>
            As configurações atuais são idênticas à proposta salva.
          </p>
        </div>
      )}

      {/* Spaces section */}
      {(diff.spacesAdded.length > 0 ||
        diff.spacesRemoved.length > 0 ||
        diff.recommendedAdded.length > 0 ||
        diff.recommendedRemoved.length > 0) && (
        <DiffSection title="Salas">
          {diff.spacesAdded.map((s) => (
            <SpaceRow
              key={s.id}
              name={s.spaceName}
              room={s.roomName}
              type="added"
            />
          ))}
          {diff.spacesRemoved.map((s) => (
            <SpaceRow
              key={s.id}
              name={s.spaceName}
              room={s.roomName}
              type="removed"
            />
          ))}
          {diff.recommendedAdded.map((s) => (
            <SpaceRow
              key={s.id}
              name={s.spaceName}
              room={s.roomName}
              type="recommended"
            />
          ))}
          {diff.recommendedRemoved.map((s) => (
            <SpaceRow
              key={s.id}
              name={s.spaceName}
              room={s.roomName}
              type="unrecommended"
            />
          ))}
        </DiffSection>
      )}

      {/* Per-space adjustments */}
      {diff.spaceAdjustmentChanges.length > 0 && (
        <DiffSection title="Alterações por sala">
          {diff.spaceAdjustmentChanges.map((sc) => (
            <SpaceAdjustmentSection key={sc.spaceId} sc={sc} />
          ))}
        </DiffSection>
      )}

      {/* Global changes */}
      {diff.globalChanges.length > 0 && (
        <DiffSection title="Configurações globais">
          {diff.globalChanges.map((c, i) => (
            <FieldChangeRow key={`${c.label}-${i}`} change={c} />
          ))}
        </DiffSection>
      )}

      {/* Diagnosis changes */}
      {diff.diagnosisChanges.length > 0 && (
        <DiffSection title="Diagnóstico">
          {diff.diagnosisChanges.map((c, i) => (
            <FieldChangeRow key={`${c.label}-${i}`} change={c} />
          ))}
        </DiffSection>
      )}

      {/* Error banner */}
      {saveStatus === "error" && errorMessage && (
        <div
          className="flex items-center gap-3 px-4 py-3 rounded-xl"
          style={{ background: "#FEF2F2", border: "1px solid #FECACA" }}
        >
          <AlertTriangle
            className="w-4 h-4 flex-shrink-0"
            style={{ color: "#DC2626" }}
          />
          <span className="text-sm flex-1" style={{ color: "#B91C1C" }}>
            {errorMessage}
          </span>
          <button
            type="button"
            onClick={handleSave}
            className="text-xs font-semibold flex-shrink-0"
            style={{ color: "#DC2626" }}
          >
            Tentar novamente
          </button>
        </div>
      )}

      {/* Save button */}
      <Button
        type="button"
        onClick={handleSave}
        disabled={saveStatus === "saving" || !diff.hasChanges}
        className="w-full gap-1.5"
        style={
          diff.hasChanges && saveStatus !== "saving"
            ? { background: "#1A1814", color: "white", borderColor: "#1A1814" }
            : {}
        }
        title={!diff.hasChanges ? "Nenhuma alteração para salvar" : undefined}
      >
        {saveStatus === "saving" ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Salvando...
          </>
        ) : (
          <>
            <Sparkles className="w-4 h-4" />
            Salvar edições na proposta
          </>
        )}
      </Button>

      {/* Conflict dialog */}
      <Dialog
        open={saveStatus === "conflict" && conflictVersion !== null}
        onOpenChange={(open) => {
          if (!open) {
            setSaveStatus("idle");
            setConflictVersion(null);
          }
        }}
      >
        <DialogContent className="max-w-sm">
          <div className="flex flex-col items-center text-center gap-3 py-2">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{ background: "#FEF3C7" }}
            >
              <AlertTriangle className="w-6 h-6" style={{ color: "#92400E" }} />
            </div>
            <DialogTitle
              className="text-base font-semibold"
              style={{ color: "#1A1814" }}
            >
              Conflito de versão
            </DialogTitle>
            <DialogDescription className="text-sm" style={{ color: "#78736D" }}>
              Esta proposta foi editada por outra pessoa enquanto você
              trabalhava (versão atual no servidor:{" "}
              {conflictVersion ?? "desconhecida"}). Recarregue a página para ver
              a versão mais recente antes de salvar.
            </DialogDescription>
            <Button
              type="button"
              className="w-full mt-1"
              style={{ background: "#1A1814", color: "white" }}
              onClick={() => {
                setConflictVersion(null);
                setSaveStatus("idle");
                navigate(".", { replace: true });
              }}
            >
              Recarregar proposta
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
